import clsx from 'clsx';
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Divider } from '@flintmint/web-shared/components/divider';
import { Collapse } from '@flintmint/web-shared/components/collapse';
import { ProgressiveBlur } from '@flintmint/web-shared/components/progressive-blur';

import styles from './ai-context-section.module.scss';

export const AIContextSection = (props: AIContextSectionProps) => {
  const {
    list,
    icon,
    open,
    title,
    header,
    onManage,
    dataCount,
    container,
    onOpenChange,
    manageTestId,
    collapseTestId,
    lastItemVisible,
    listMaxHeightOffset,
  } = props;
  const sectionKey = title.toLowerCase().replace(/[^a-z0-9]+/g, '-');

  const lastItemRef = useRef<HTMLDivElement>(null);
  const [isLastItemVisible, setIsLastItemVisible] = useState(false);
  const isFooterBlurHidden = lastItemVisible ?? isLastItemVisible;

  useEffect(() => {
    const el = lastItemRef.current;

    if (!el) return;

    const observer = new IntersectionObserver(([entry]) => setIsLastItemVisible(entry.isIntersecting), {
      threshold: 1,
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [open, dataCount]);

  return (
    <div
      className={clsx(styles.aiContextSection, {
        [styles.open]: open,
      })}
      style={listMaxHeightOffset ? ({ '--list-height-offset': `${listMaxHeightOffset}px` } as CSSProperties) : undefined}
    >
      <Collapse
        open={open}
        iconSize={25}
        onChange={onOpenChange}
        labelTestId={collapseTestId}
        label={
          <div className={styles.aiContextSectionHeader}>
            <Icon type={icon} size={25} multiColor />
            <span>{title}</span>
            {!!dataCount.total && (
              <small className="badge" data-testid={`ai-context-badge-${sectionKey}`}>
                {dataCount.enabled}
                <span className={styles.badgeTotal}>/{dataCount.total}</span>
              </small>
            )}
          </div>
        }
        className={styles.collapseContent}
        holderClassName={styles.collapseHolder}
        labelHolderClassName={styles.labelHolder}
      >
        <Divider orientation="horizontal" size="100%" className={styles.divider} />
        {!!header && <div className={styles.headerHolder}>{header}</div>}
        {!!list && (
          <div className={styles.list}>
            {list}
            <div ref={lastItemRef} />
          </div>
        )}
        {container}
        <div
          className={clsx(styles.footer, {
            [styles.lastItemVisible]: isFooterBlurHidden,
          })}
        >
          <div>
            <ProgressiveBlur position="bottom" intensity={20} />
            <button
              data-testid={manageTestId ?? `ai-context-manage-${sectionKey}`}
              className={styles.manageBtn}
              onClick={onManage}
            >
              Expand <Icon type="arrow-expand" size={15} />
            </button>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

export interface AIContextSectionProps {
  icon: string;
  open: boolean;
  title: string;
  list?: ReactNode;
  container?: ReactNode;
  dataCount: { enabled: number; total: number };
  onManage: () => void;
  collapseTestId?: string;
  manageTestId?: string;
  header?: ReactNode;
  lastItemVisible?: boolean;
  listMaxHeightOffset?: number;
  onOpenChange?: (open: boolean) => void;
}
